import { z } from "zod"

import type {
  ImageRef,
  ShowcaseBannerStyle,
  ShowcaseMastheadLayout,
  StoreConfig,
} from "./types"

export const SHOWCASE_MASTHEAD_LAYOUTS = [
  "centered",
  "split",
  "stacked",
] as const satisfies readonly ShowcaseMastheadLayout[]

export const SHOWCASE_BANNER_STYLES = [
  "square",
  "rounded",
] as const satisfies readonly ShowcaseBannerStyle[]

/** The `showcase` top bar's background when the tenant sets none. */
export const DEFAULT_SHOWCASE_TOP_BAR_BACKGROUND = "#000000"

/** `showcase` settings with every default applied and every colour validated. */
export interface ResolvedShowcaseSettings {
  topBar: {
    background: string
    /** null ⇒ derive a contrasting colour from `background`. */
    foreground: string | null
    showLeadTimes: boolean
  }
  masthead: {
    layout: ShowcaseMastheadLayout
    /** null ⇒ transparent: the page's backdrop photo shows through. */
    background: string | null
    /** null ⇒ contrast with `background`, or the page text colour without one. */
    foreground: string | null
    image: ImageRef | null
  }
  nav: {
    /** null ⇒ the tenant primary (with its own primary-foreground). */
    background: string | null
    foreground: string | null
  }
  /** null ⇒ derived from the theme `accent` by the layout. */
  highlight: string | null
  bannerStyle: ShowcaseBannerStyle
  /** null ⇒ the layout's own footer artwork. */
  footerImage: ImageRef | null
}

/**
 * A plain CSS colour: hex, a colour function (`rgb()`, `oklch()`, …) or a
 * keyword. These values end up in inline styles, so anything that could
 * close a declaration or rule (`; { } < >` and backslashes), pull in a
 * resource (`url(`) or run an old-IE expression is rejected — the field then
 * falls back to its default.
 */
const cssColorSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(/^[#(),.%/\sA-Za-z0-9-]+$/)
  .refine((value) => !/url\s*\(|expression\s*\(/i.test(value))

const imageSchema = z
  .object({ url: z.string().trim().min(1), alt: z.string().optional() })
  .transform((image): ImageRef => image)

const showcaseSettingsSchema = z
  .object({
    topBarBackground: cssColorSchema.optional().catch(undefined),
    topBarForeground: cssColorSchema.optional().catch(undefined),
    showLeadTimes: z.boolean().optional().catch(undefined),
    mastheadLayout: z
      .enum(SHOWCASE_MASTHEAD_LAYOUTS)
      .optional()
      .catch(undefined),
    mastheadBackground: cssColorSchema.optional().catch(undefined),
    mastheadForeground: cssColorSchema.optional().catch(undefined),
    headerImage: imageSchema.optional().catch(undefined),
    navBackground: cssColorSchema.optional().catch(undefined),
    navForeground: cssColorSchema.optional().catch(undefined),
    highlight: cssColorSchema.optional().catch(undefined),
    bannerStyle: z.enum(SHOWCASE_BANNER_STYLES).optional().catch(undefined),
    footerImage: imageSchema.optional().catch(undefined),
  })
  .partial()
  .catch({})

/**
 * Narrow a tenant's raw `showcase` settings: unknown enum values, malformed
 * colours and image refs without a url each fall back to that field's
 * default, so one bad value never costs the rest (or breaks the page).
 */
export function resolveShowcaseSettings(
  config: Pick<StoreConfig, "showcase">
): ResolvedShowcaseSettings {
  const raw = showcaseSettingsSchema.parse(config.showcase ?? {})
  return {
    topBar: {
      background: raw.topBarBackground ?? DEFAULT_SHOWCASE_TOP_BAR_BACKGROUND,
      foreground: raw.topBarForeground ?? null,
      showLeadTimes: raw.showLeadTimes ?? false,
    },
    masthead: {
      layout: raw.mastheadLayout ?? "centered",
      background: raw.mastheadBackground ?? null,
      foreground: raw.mastheadForeground ?? null,
      image: raw.headerImage ?? null,
    },
    nav: {
      background: raw.navBackground ?? null,
      foreground: raw.navForeground ?? null,
    },
    highlight: raw.highlight ?? null,
    bannerStyle: raw.bannerStyle ?? "square",
    footerImage: raw.footerImage ?? null,
  }
}
